import { DateTime } from "luxon";
import { type RefObject, type SyntheticEvent, useMemo, useState } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import { useLocationCredentialsQueryHook } from "../../../pages/Profile/v2/SynergyCredentials/credentialsHooks";
import { useAddSingleShiftByDateAndNurse } from "../../../pages/Profile/v2/WeeklySchedule/XWeekScheduleGrid/hooks";
import { isNurseActiveInDepartment } from "../../../utils/formatters";
import { ChangesSource, type DepartmentLocation, type Nurse } from "../../../utils/models";
import { facilityToString, getDayNameByIndex, getUniqueLocations } from "../../../utils/utilFunctions";
import { useCreateOrUpdateMultipleRecurringShifts } from "../../RecurringShift/RecurringShiftMasterSchedule/hooks";
import type { SystemAutoCompleteOption } from "../../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";

type Props = {
	mode: "add-recurring" | "add-single";
	facility?: DepartmentLocation;
	clickedDate: DateTime;
	onClose?: ((didUpdateOrCreate?: boolean) => void) | ((didUpdateOrCreate?: boolean) => Promise<void>);
	didChangeRef?: RefObject<boolean>;
	onWarnOnClose?: () => void;
};

export const useSynergyMinimalShiftAdditionState = ({ mode, facility, clickedDate, onClose, didChangeRef, onWarnOnClose }: Props) => {
	const { track } = useAnalytics();
	const [currentDate, setCurrentDate] = useState<DateTime>(clickedDate);
	const { mutateAsync: handleCreateOrUpdateRecurringShifts, isPending: isCreateOrUpdateRecurringShiftsLoading } = useCreateOrUpdateMultipleRecurringShifts();
	const { hospitalData, chosenDepartment } = useHospitalData();
	const formatPersonName = useFormatPersonName();
	const { addShift: addSingleShift, isLoading: isAddingSingleShift } = useAddSingleShiftByDateAndNurse({
		selectedDate: currentDate,
		onCloseCalled: () => onClose?.(true),
	});

	const activeNurses = hospitalData.nurses
		.filter((nurse) => isNurseActiveInDepartment(nurse, chosenDepartment.department_id))
		.map<SystemAutoCompleteOption<Nurse>>((nurse) => ({
			value: nurse,
			label: formatPersonName(nurse),
		}));

	const facilitiesList = getUniqueLocations(chosenDepartment.locations, false, true)
		.filter((location) => !!location.location)
		.sort((locA, locB) => locA.location.localeCompare(locB.location))
		.map<SystemAutoCompleteOption<DepartmentLocation>>((location) => ({
			value: location,
			label: facilityToString(location),
		}));

	const [state, setState] = useState<{
		selectedNurse: SystemAutoCompleteOption<Nurse> | null;
		selectedFacility: SystemAutoCompleteOption<DepartmentLocation> | null;
		showCredentialedOnly: boolean;
		createForBothWeeks: boolean;
	}>({
		selectedNurse: null,
		selectedFacility: facilitiesList.find((curr) => curr.value.id === facility?.id) || null,
		showCredentialedOnly: true,
		createForBothWeeks: false,
	});

	const handleSetState = (newState: Partial<typeof state>) => {
		setState((prevState) => ({ ...prevState, ...newState }));
		if (didChangeRef) {
			didChangeRef.current = true;
		}
	};

	const { credentialedNurseIds, isLoading: isLoadingCredentials } = useLocationCredentialsQueryHook({
		locationId: state.selectedFacility?.value.id,
		hideExpired: true,
		onlyShowApproved: true,
	});

	const handleNurseChange = (_: SyntheticEvent, value: SystemAutoCompleteOption<Nurse> | null) => {
		handleSetState({
			selectedNurse: value,
		});
	};

	const onSelectedFacilityChange = (_: SyntheticEvent, facility: SystemAutoCompleteOption<DepartmentLocation> | null) => {
		if (facility) {
			handleSetState({
				selectedFacility: facility,
			});
		}
	};

	const toggleShowCredentialedOnly = (checked: boolean) => {
		if (checked && state.selectedNurse) {
			if (!credentialedNurseIds.includes(state.selectedNurse.value.id)) {
				handleSetState({
					selectedNurse: null,
					showCredentialedOnly: checked,
				});
			} else {
				handleSetState({
					showCredentialedOnly: checked,
				});
			}
		} else {
			handleSetState({
				showCredentialedOnly: checked,
			});
		}
	};

	const saveDisabled = !state.selectedNurse || !state.selectedFacility;

	const handleSaveClick = async () => {
		if (!state.selectedNurse || !state.selectedFacility) {
			return;
		}
		const time = currentDate.set({ hour: 13, minute: 0 });
		track("synergy_minimal_shift_addition_creating_shift", {
			mode: mode,
			clickedDate: currentDate.toISODate(),
			startTime: time.toISOTime(),
			endTime: time.toISOTime(),
			location: state.selectedFacility.value.location,
			departmentId: chosenDepartment.department_id,
			nurseId: state.selectedNurse.value.id,
		});
		if (mode === "add-single") {
			// Use useAddSingleShiftByDateAndNurse for single shift creation
			await addSingleShift(state.selectedNurse.value, time, time, state.selectedFacility.value.location);
			track("synergy_minimal_shift_addition_created_single_shift", {
				mode: mode,
				clickedDate: currentDate.toISODate(),
				startTime: time.toISOTime(),
				endTime: time.toISOTime(),
				location: state.selectedFacility.value.location,
				departmentId: chosenDepartment.department_id,
				nurseId: state.selectedNurse.value.id,
			});
		} else {
			// Use useCreateOrUpdateMultipleRecurringShifts for recurring shift creation
			const res = await handleCreateOrUpdateRecurringShifts({
				shiftsToRemove: [],
				existingRecurringShifts: [],
				source: ChangesSource.MasterSchedule,
				newRecurringShifts: [
					{
						recurringParams: {
							recurringType: "Weekly",
							weekdays: [getDayNameByIndex(currentDate.weekday % 7).toLowerCase()],
							interval: state.createForBothWeeks ? 1 : 2,
						},
						location: state.selectedFacility.value.location,
						nurseId: state.selectedNurse.value.id,
						deleted: false,
						departmentId: chosenDepartment.department_id,
						startDate: (state.createForBothWeeks ? DateTime.now() : currentDate).startOf("week").toFormat("yyyy-MM-dd"),
						shiftStart: time.toFormat("HH:mm"),
						shiftEnd: time.toFormat("HH:mm"),
					},
				],
				departmentId: chosenDepartment.department_id,
				nurseId: state.selectedNurse.value.id,
			});
			if (res.status === 200 || res.status === 201) {
				track("synergy_minimal_shift_addition_created_recurring_shift", {
					mode: mode,
					clickedDate: currentDate.toISODate(),
					startTime: time.toISOTime(),
					endTime: time.toISOTime(),
					location: state.selectedFacility.value.location,
					departmentId: chosenDepartment.department_id,
					nurseId: state.selectedNurse.value.id,
				});
				onClose?.(true);
			}
		}
	};

	const handleCloseClick = () => {
		if (didChangeRef?.current && onWarnOnClose) {
			onWarnOnClose();
		} else {
			onClose?.(false);
		}
	};

	const nurseListToShow = useMemo(
		() => (state.showCredentialedOnly ? activeNurses.filter((nurseOption) => credentialedNurseIds.includes(nurseOption.value.id)) : activeNurses),
		[state.showCredentialedOnly, activeNurses, credentialedNurseIds],
	);

	const isLoading = mode === "add-single" ? isAddingSingleShift : isCreateOrUpdateRecurringShiftsLoading;

	return {
		state,
		handleNurseChange,
		credentialedNurseIds,
		facilitiesList,
		onSelectedFacilityChange,
		toggleShowCredentialedOnly,
		saveDisabled,
		handleSaveClick,
		isCreateOrUpdateRecurringShiftsLoading: isLoading,
		handleCloseClick,
		handleSetState,
		isLoadingCredentials,
		nurseListToShow,
		currentDate,
		setCurrentDate,
	};
};
